/**
 * The menu screen.
 *
 * This is what a customer sees after scanning the QR at their table, so it is
 * the most performance-sensitive screen in the product: it must render the menu
 * fast on a mid-range phone on restaurant wifi. Everything it displays comes
 * from `public_menu`, which has already resolved discounts, sold-out state, and
 * time-of-day availability, so this screen does no pricing arithmetic.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { View } from 'react-native';
import type { CartOption, PublicMenuItem } from '@yummy/shared';
import {
  Button,
  Row,
  EmptyState,
  LoadingState,
  Screen,
  SegmentedControl,
  Stack,
  Text,
  cn,
  useResponsive,
  useT,
} from '@yummy/ui-rnr';

import { useCart } from '../app/cart';
import { useMenuCategory } from '../app/menuCategory';
import { peekMenuFootCategory, rememberMenuFoot, takeMenuFoot } from '../app/menuReturn';
import { rememberTableToken, useStorefront, type StorefrontSource } from '../app/storefront';
import { AppBar } from '../components/AppBar';
import { CartBar } from '../components/CartBar';
import { FullScreenMessage } from '../components/FullScreenMessage';
import { MenuItemRow } from '../components/MenuItemRow';
import { RestaurantHeader } from '../components/RestaurantHeader';
import { PackageSearch, TriangleAlert } from 'lucide-react-native';


/**
 * Renders a restaurant's menu for a scanned table token or a slug.
 *
 * @param props The storefront source the route resolved.
 * @returns The menu screen.
 */
/**
 * How tall the pinned rows are: where the tracking line sits and where a jump
 * lands. The tab row is `h-12` and the section headings pin directly under it,
 * which is what `top-12` and `scroll-mt-12` say in the markup.
 */
const PINNED_HEIGHT = 48;

/**
 * How much air to leave between the My orders button and the bottom of the list
 * when the menu reopens at its foot, matching the screen's own gutter.
 */
const FOOT_CLEARANCE = 16;

/**
 * The nearest ancestor that actually scrolls.
 *
 * The menu's content scrolls inside the screen's scroll view rather than the
 * page, and that element carries no ref or id of its own — so it is found by
 * walking up from something inside it.
 *
 * @param node An element inside the scroller.
 * @returns The scroller, or null when there is none.
 */
function scrollParent(node: HTMLElement | null): HTMLElement | null {
  for (let el = node?.parentElement ?? null; el; el = el.parentElement) {
    const style = getComputedStyle(el);
    if (
      (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
      el.scrollHeight > el.clientHeight
    ) {
      return el;
    }
  }
  return null;
}

export function MenuScreen({ source }: { source: StorefrontSource }) {
  const t = useT();
  const { isTablet } = useResponsive();
  const navigate = useNavigate();
  const location = useLocation();
  const cart = useCart();
  const storefront = useStorefront(source);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  /**
   * The section the menu was left on, when this mount is the return from My
   * orders.
   *
   * Read at the first render, before the strip paints: the scroll has not
   * reported yet, and the strip marks a section from the first frame. A render
   * can only peek — the effect below takes the flag.
   */
  const [footCategory] = useState(peekMenuFootCategory);
  /**
   * Whether the tab row is showing.
   *
   * Nothing to jump to until the list has moved: at the top of the menu the
   * categories are all ahead, and the row would only repeat the headings below
   * it. It floats, so coming and going moves nothing.
   */
  const [tabsShowing, setTabsShowing] = useState(false);
  /** True once the hero's name has gone behind the header, which then carries it. */
  const [nameInBar, setNameInBar] = useState(false);
  /** The app bar alone: the stable thing the tab row's arrival is measured against. */
  const [barHeight, setBarHeight] = useState(PINNED_HEIGHT);
  /** Until this moment, scroll position is ignored in favour of the last tap. */
  const ignoreScrollUntil = useRef(0);
  // Read from the restaurant, which is not known until the storefront loads;
  // the effect below picks it up as soon as it is.
  const { category: lastCategory, remember } = useMenuCategory(
    storefront.data?.restaurant.id ?? null,
  );

  // Remember the sticker so checkout can attribute the order to it, even though
  // checkout is a separate route.
  useEffect(() => {
    if (source.kind === 'token') rememberTableToken(source.value);
  }, [source.kind, source.value]);

  const sections = useMemo(() => {
    const menu = storefront.data?.menu;
    if (!menu) return [];

    const withItems = menu.categories.filter((category) => category.items.length > 0);
    const sections = withItems.map((category) => ({
      id: category.id,
      name: category.name,
      description: category.description,
      items: category.items,
    }));

    if (menu.uncategorised.length > 0) {
      sections.push({
        id: '__uncategorised__',
        name: t('customer.menu.other'),
        description: null,
        items: menu.uncategorised,
      });
    }

    return sections;
    // `t` is a dependency because the uncategorised section's label is
    // translated: switching language must rebuild the section list.
  }, [storefront.data, t]);

  /**
   * The section this mount opens on, when it is the return from My orders.
   *
   * The button that leaves for My orders sits at the foot of the list, so the
   * section the customer was in is the one the strip was marking — recorded on
   * the way out and taken back here. It is the answer the scroll would arrive at
   * once the restored position settles, which is why the strip has nothing to
   * correct: it opens on this chip and the scroll agrees. A section that has
   * since left the menu falls back to the last one, which is where the foot is.
   */
  const returningTo =
    footCategory === null
      ? null
      : sections.some((section) => section.id === footCategory)
        ? footCategory
        : (sections[sections.length - 1]?.id ?? null);

  // Scrolling is where the current section comes from — a tab that only
  // reflected the last tap would be wrong the moment the customer scrolled.
  useEffect(() => {
    if (typeof document === 'undefined' || sections.length < 2) return;

    let frame = 0;
    const track = () => {
      frame = 0;

      // The list's own start, not the header block's height: the block grows
      // when the tab row arrives, and measuring against it would flip the row on
      // and off at the boundary. A little hysteresis keeps it from flickering.
      // Past the first heading, not merely level with it: the row arrives once
      // that heading has gone behind the bar, so the two never say the same
      // category at the same time.
      // Viewport coordinates here, unlike a jump: the sections are measured
      // against the screen, so the bar counts as well as the row.
      const line =
        (document.getElementById('menu-header')?.getBoundingClientRect().height ?? 0) +
        (document.getElementById('menu-tabs')?.getBoundingClientRect().height ?? 0);
      const firstSection = document.querySelector<HTMLElement>('[id^="menu-section-"]');
      const headingBottom = firstSection?.firstElementChild?.getBoundingClientRect().bottom ?? 0;
      setTabsShowing((showing) =>
        showing ? headingBottom <= line + PINNED_HEIGHT : headingBottom <= line,
      );

      // The bar takes the restaurant's name over exactly as the hero's copy
      // leaves the screen, so the name is always somewhere and never twice.
      const hero = document.getElementById('menu-restaurant-name');
      setNameInBar(hero !== null && hero.getBoundingClientRect().bottom <= barHeight);

      const marks = document.querySelectorAll<HTMLElement>('[id^="menu-section-"]');
      let current: string | null = null;
      for (const mark of marks) {
        if (mark.getBoundingClientRect().top > line + 1) break;
        current = mark.id.replace('menu-section-', '');
      }
      // Nothing above the bar means the customer is in the header, looking at
      // the top of the list — so the first section is where they are. Leaving
      // the tab on the last section it saw made it lie after a quick scroll
      // back to the top, which is exactly when it is the only thing on screen.
      setActiveCategory(current ?? sections[0]!.id);
    };
    const onScroll = () => {
      // A jump passes every section between here and there; the tab was already
      // set by the tap, and following the scroll would strobe it through them.
      if (Date.now() < ignoreScrollUntil.current) return;
      if (!frame) frame = requestAnimationFrame(track);
    };

    // Scroll does not bubble, but it does capture: listening on the document
    // catches the screen's own scroller without reaching for its ref.
    document.addEventListener('scroll', onScroll, true);
    track();
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [sections, barHeight]);

  /**
   * Puts a section under the pinned rows, and again once the page has settled.
   *
   * The header holds its height whether or not the tab row is showing, so a jump
   * is aimed at geometry that will still be true when it arrives. It used to be
   * corrected after the fact instead, which fired an instant scroll in the middle
   * of the smooth one and overshot by several hundred points.
   */
  const landOn = useCallback(
    (sectionId: string, smooth: boolean) => {
      if (typeof document === 'undefined') return;
      const element = document.getElementById(`menu-section-${sectionId}`);
      if (!element) return;

      // Only the floated row. The app bar sits *above* the scrollport — the
      // list begins below it — so a landing measured against the scrollport has
      // already cleared the bar, and adding it again put every jump exactly one
      // bar-height too low. That 52 was the whole bug.
      const tabs =
        document.getElementById('menu-tabs')?.getBoundingClientRect().height ?? 0;
      const clear = Math.round(tabs);
      element.style.setProperty('scroll-margin-top', `${clear}px`);

      element.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'auto' });
    },
    [],
  );

  // Keep the selected tab valid as the menu loads or changes, and take up the
  // one this restaurant was left on. The scroll is deliberately not restored:
  // scrolling on mount meant a plain refresh landed wherever the list was last
  // left — at the bottom of it, for anyone who had read that far — instead of at
  // the top where a refresh is expected to begin. The one exception is the foot
  // below, which is a place rather than an offset.
  useEffect(() => {
    if (sections.length === 0) return;
    const isTab = (id: string | null) => Boolean(id && sections.some((s) => s.id === id));
    if (isTab(activeCategory)) return;

    setActiveCategory(
      // Opening at the foot is a place rather than an offset, and the section
      // the customer was in came back with it. Saying so here, instead of
      // letting the scroll report it a frame later, is half of what lets the
      // strip paint on the right chip with nothing to correct — the other half
      // is the strip refusing to animate its first placement.
      returningTo ?? (isTab(lastCategory) ? lastCategory! : sections[0]!.id),
    );
  }, [sections, activeCategory, lastCategory, returningTo]);

  /**
   * The chip the strip marks.
   *
   * The scroll is where this normally comes from — a tab that only reflected the
   * last tap would be wrong the moment the customer scrolled. The exception is
   * the mount that opens at the foot: the scroll has not reported yet, and the
   * section is the one carried back from the button. Marking it from the first
   * frame is what leaves nothing to correct; `activeCategory` takes over the
   * moment the scroll speaks, and says the same thing.
   */
  const markedCategory = activeCategory ?? returningTo;

  // Coming back from My orders, open at the foot of the menu. That button is the
  // last thing in the list, so it could only have been tapped from there — the
  // bottom is where the customer was, and no offset has to be remembered. A
  // refresh never set the flag, so a refresh still opens at the top.
  useEffect(() => {
    if (typeof document === 'undefined' || sections.length === 0 || !takeMenuFoot()) return;

    const toFoot = () => {
      const button = document.getElementById('menu-my-orders');
      if (!button) return;
      button.scrollIntoView({ block: 'end' });
      // `end` lands the button flush on the scrollport edge — against the screen,
      // or against the cart bar when the cart has something in it. A gutter of
      // air reads better, and matches the screen's own 16.
      scrollParent(button)?.scrollBy({ top: FOOT_CLEARANCE });
    };

    toFoot();
    // Once more after the covers have laid out, which lengthens the list above.
    const settle = setTimeout(toFoot, 350);
    return () => clearTimeout(settle);
  }, [sections]);

  if (storefront.initial) {
    return (
      <Screen>
        <LoadingState message={t('common.loading')} />
      </Screen>
    );
  }

  if (storefront.error || !storefront.data) {
    return (
      <FullScreenMessage
        icon={TriangleAlert}
        tone="danger"
        title={t('customer.menu.loadFailed')}
        message={storefront.error ?? t('customer.menu.loadFailedHint')}
        actionLabel={t('common.retry')}
        onAction={storefront.reload}
      />
    );
  }

  const { restaurant, menu, resolution } = storefront.data;

  /**
   * Scrolls to a section, and remembers it.
   *
   * Only a deliberate tap is recorded — not the section the scroll happens to
   * land in — or arriving somewhere would overwrite the place the customer
   * actually left.
   */

  const jumpTo = (sectionId: string) => {
    setActiveCategory(sectionId);
    remember(sectionId);
    ignoreScrollUntil.current = Date.now() + 700;

    landOn(sectionId, true);
  };

  const addItem = (item: PublicMenuItem, options: CartOption[] = [], quantity = 1) => {
    if (cart.conflictsWith(restaurant.id)) {
      // Replacing the cart silently would lose work; ask first.
      const confirmed =
        typeof window === 'undefined' ? true : window.confirm(t('customer.menu.switchRestaurant'));
      if (!confirmed) return;
    }
    cart.addMany(restaurant.id, item, options, quantity);
  };

  const myOrdersLabel = t('customer.menu.myOrders');


  return (
    <Screen
      // The bar carries the language and appearance control and nothing else.
      //
      // No back control: this is where the QR puts the customer, so there is
      // usually no page behind it to return to — and on the occasion there is,
      // it is a landing page they did not ask for. Nothing behind the bar needs
      // one either, since the restaurant's own name is the hero below it and the
      // cart bar at the other end is the way onward.
      // `pt-0`: the header sits directly above the content now, so the screen's
      // usual top padding left a 16px band between the app bar and the cover.
      contentClassName="pt-0"
      header={
        <View
          nativeID="menu-header"
          // Above the list. The row floats out of the flow, and without a
          // stacking order the scroller — which comes after the header — paints
          // over it, leaving the dishes showing through the bar.
          className="z-20">
          <View onLayout={(event) => setBarHeight(event.nativeEvent.layout.height)}>
            <AppBar title={nameInBar ? restaurant.name : undefined} />
          </View>
          {sections.length > 1 ? (
            // Floated over the list rather than stacked in the header. In the
            // flow it pushed the dishes down the moment it appeared — that is
            // the bounce — and holding its space open instead left an empty bar.
            // Out of the flow it can come and go without moving anything, which
            // is also what lets the header be one height for a jump to measure.
            <View
              nativeID="menu-tabs"
              style={{ top: barHeight }}
              className={cn(
                'bg-background absolute right-0 left-0 px-4 pb-2',
                !tabsShowing && 'pointer-events-none opacity-0',
              )}>
              <SegmentedControl
                variant="brand"
                options={sections.map((section) => ({ value: section.id, label: section.name }))}
                value={markedCategory ?? sections[0]!.id}
                onChange={jumpTo}
              />
            </View>
          ) : null}
        </View>
      }
      footer={
        <CartBar
          itemCount={cart.restaurantId === restaurant.id ? cart.itemCount : 0}
          subtotalCents={cart.restaurantId === restaurant.id ? cart.subtotalCents : 0}
          currency={restaurant.currency}
          onReview={() => {
            // Checkout is overlaid on this screen: the location behind it comes
            // along so the menu is still mounted, and still scrolled, while the
            // checkout is up. There is no position to remember or restore.
            navigate(`/r/${restaurant.slug}/checkout`, {
              state: { backgroundLocation: location },
            });
          }}
          hint={restaurant.can_order_now ? undefined : t('customer.menu.scheduleHint')}
        />
      }>
      <Stack gap="lg">
        <RestaurantHeader restaurant={restaurant} />

        {resolution.kind === 'table' && resolution.table_label ? (
          <Text className="text-subtle-foreground text-sm">
            {t('customer.menu.orderingFrom', { table: resolution.table_label })}
          </Text>
        ) : null}

        {sections.length === 0 ? (
          <EmptyState
            icon={PackageSearch}
            title={t('customer.menu.empty')}
            message={t('customer.menu.emptyHint')}
          />
        ) : (
          <>
            {sections.map((section) => (
              // `scroll-mt-12` is where a jump lands: the section's top stops
              // under the pinned rows rather than behind them.
              <View
                key={section.id}
                nativeID={`menu-section-${section.id}`}
                className="scroll-mt-12">
                {/* A grey band, full-bleed past the list's gutters. It separates
                    by being a different layer from the rows rather than a louder
                    version of them: at the dish's own size and weight, a heading
                    that is merely bigger reads as another dish name. */}
                <View className={cn('bg-muted -mx-4 mb-8 px-4 py-3', isTablet && '-mx-6 px-6')}>
                  {/* The count sits on the name's line, at the far end, so the
                      name keeps the left edge and the description keeps a line
                      of its own. */}
                  <Row spread gap="sm" align="baseline">
                    {/* 18/600, the section heading every other screen uses.
                        At 16/700 this was byte-identical to the dish names
                        under it, so a category did not lead its own list. */}
                    <Text className="text-foreground text-lg font-semibold">{section.name}</Text>
                    <Text className="text-muted-foreground text-sm">
                      {t('common.itemCount', { count: section.items.length })}
                    </Text>
                  </Row>
                  {section.description ? (
                    <Text className="text-muted-foreground text-sm">{section.description}</Text>
                  ) : null}
                </View>

                <Stack gap="lg">
                  {section.items.map((item, index) => (
                    <View
                      key={item.id}
                      className={cn(
                        'pb-4',
                        // The last dish of a section is followed by the next
                        // section's band, which divides them already.
                        index < section.items.length - 1 && 'border-border border-b',
                      )}>
                      <MenuItemRow
                        item={item}
                        currency={restaurant.currency}
                        timezone={restaurant.timezone}
                        onAdd={(options, quantity) => addItem(item, options, quantity)}
                      />
                    </View>
                  ))}
                </Stack>
              </View>
            ))}
          </>
        )}

        <Stack gap="sm" align="center" className="p-4">
          <Text className="text-subtle-foreground text-center text-xs">
            {t('customer.menu.pricesNote', {
              currency: menu.currency,
              taxNote:
                restaurant.tax_rate_bps > 0
                  ? t('customer.menu.taxNote', {
                      rate: (restaurant.tax_rate_bps / 100).toFixed(0),
                    })
                  : '',
            })}
          </Text>
          <Button
            variant="ghost"
            size="sm"
            nativeID="menu-my-orders"
            onPress={() => {
              // Leave a note, so the menu reopens here rather than at the top,
              // and say where the list was opened from, so an order can step all
              // the way back to this menu instead of replacing itself with one.
              // The section comes along: the strip is marking it, and the scroll
              // would only report it again once the restore had settled.
              rememberMenuFoot(markedCategory ?? null);
              navigate('/orders', { state: { from: 'menu' } });
            }}
            accessibilityLabel={myOrdersLabel}>
            <Text>{myOrdersLabel}</Text>
          </Button>
        </Stack>
      </Stack>
    </Screen>
  );
}
